import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { NAME_CHAR_LIMIT } from '../../../../../../corelogic/domain/constants/fieldLimits.constants';
import { TokenNotFoundError } from '../../../../../../corelogic/domain/errors/authentication.error';
import { USER_USECASE_ERROR_MESSAGES } from '../../../../../../corelogic/domain/errors/usecases.errorMessages/userUsecase.error.messages';
import { UserUpdateError } from '../../../../../../corelogic/domain/errors/user.error';
import {
  EditableProfileFields,
  UserProfile as UserProfileType,
} from '../../../../../../corelogic/domain/models/user.model';
import { ValidationResult } from '../../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';
import { store } from '../../../../../../corelogic/state/store';
import {
  setUserError,
  updateUserProfile,
} from '../../../../../../corelogic/state/user/user.action';
import { UPDATE_USER_PROFILE_SUCCESS } from '../../../../../../corelogic/state/user/user.action.type';
import { ConfirmationModal } from '../../ConfirmationModal/ConfirmationModal';
import { SecureTextField } from '../../Shared/SecureTextField/SecureTextField';

const styles = {
  form: {
    width: '100%',
  },
  field: {
    mb: 2,
    '& .MuiInputBase-root': {
      color: 'white',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 1,
      fontSize: 'clamp(0.85rem, 1vw, 0.85rem)',
      height: '48px',
      padding: '0.75rem 1rem',
    },
    '& .MuiFormLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#8B0000',
      },
    },
    '& input[type="date"]': {
      height: '100%',
      paddingTop: '24px',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
  },
  cancelButton: {
    color: 'rgba(255, 255, 255, 0.7)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
  },
  saveButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    fontWeight: 500,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#aa0000',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.5)',
      transform: 'translateY(1px)',
    },
  },
  disabledField: {
    opacity: 0.7,
    '& .MuiInputBase-root': {
      color: 'rgba(255, 255, 255, 0.5)',
    },
  },
};

interface SettingsEditFormProps {
  user: UserProfileType;
  token: string;
  isUserLoading: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const SettingsEditForm: React.FC<SettingsEditFormProps> = ({
  user,
  token,
  isUserLoading,
  onCancel,
  onSuccess,
}) => {
  // Local state management
  const [formData, setFormData] = useState<EditableProfileFields>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    gender: user?.gender || '',
    dateOfBirth: user?.dateOfBirth || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingProfileUpdate, setPendingProfileUpdate] = useState(false);

  // Security validation state
  const [fieldValidations, setFieldValidations] = useState<
    Record<string, ValidationResult>
  >({});

  // Listen for successful change nationality to show success modal
  useEffect(() => {
    const subscription = store.action$.subscribe((action) => {
      if (action.type === UPDATE_USER_PROFILE_SUCCESS && pendingProfileUpdate) {
        setPendingProfileUpdate(false);
        //Success modal is shown in the parent component
        onSuccess();
      }
    });

    return () => subscription.unsubscribe();
  }, [pendingProfileUpdate, onSuccess]);

  // Update formData when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        gender: user.gender || '',
        dateOfBirth: user.dateOfBirth || '',
      });
    }
  }, [user]);

  // Check if form data has changed from original user data
  useEffect(() => {
    if (user) {
      const hasChanged = Boolean(
        formData.firstName !== user.firstName ||
          formData.lastName !== user.lastName ||
          formData.gender !== user.gender ||
          (formData.dateOfBirth &&
            user.dateOfBirth &&
            formData.dateOfBirth.split('T')[0] !==
              user.dateOfBirth.split('T')[0]),
      );

      setIsDirty(hasChanged);
    }
  }, [formData, user]);

  // Generic secure change handler for SecureTextField components
  const handleSecureChange = (
    field: keyof EditableProfileFields,
    value: string,
    validation: ValidationResult,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldValidations((prev) => ({ ...prev, [field]: validation }));
  };

  // Simple field change handler for non-secure fields
  const handleFieldChange = (
    field: keyof EditableProfileFields,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Check if there are any high-severity security issues
  const hasSecurityIssues = () => {
    return Object.values(fieldValidations).some((validation) =>
      validation.warnings.some((warning) => warning.severity === 'high'),
    );
  };

  const resetForm = useCallback(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        gender: user.gender || '',
        dateOfBirth: user.dateOfBirth || '',
      });
    }
    onCancel();
  }, [user, onCancel]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      resetForm();
    }
  }, [isDirty, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setPendingProfileUpdate(true);

    const requiredFields = ['firstName', 'lastName'];

    // Check for empty fields before dispatching
    const emptyRequiredFields = requiredFields.filter((key) => {
      const k = key as keyof typeof formData;
      const value = formData[k];

      return typeof value === 'string' ? value.trim() === '' : !value;
    });

    if (emptyRequiredFields.length > 0) {
      store.dispatch(
        setUserError(
          `Fields cannot be empty: ${emptyRequiredFields.join(', ')}`,
        ),
      );
      setIsSubmitting(false);
      setPendingProfileUpdate(false);
      return;
    }

    // Optional fields that shouldn't be blanked out if they had values
    const optionalFieldsWithValues = ['gender', 'dateOfBirth'].filter(
      (field) => {
        const key = field as keyof typeof formData;
        // Check if the original user data had a value for this field
        const originalValue = user?.[key];
        // Check if the form data is trying to blank out a previously filled field
        const newValue = formData[key];

        return originalValue && (!newValue || newValue === '');
      },
    );

    if (optionalFieldsWithValues.length > 0) {
      store.dispatch(
        setUserError(
          `These fields cannot be emptied once set: ${optionalFieldsWithValues.join(', ')}`,
        ),
      );
      setIsSubmitting(false);
      setPendingProfileUpdate(false);
      return;
    }

    try {
      if (!user || !token) {
        store.dispatch(
          setUserError(
            new TokenNotFoundError(USER_USECASE_ERROR_MESSAGES.UNAUTHORIZED)
              .message,
          ),
        );
        setIsSubmitting(false);
        setPendingProfileUpdate(false);
        return;
      }

      await store.dispatch(
        updateUserProfile({
          token,
          currentProfile: user,
          newData: formData as EditableProfileFields,
          retry: 0,
        }),
      );
    } catch (error) {
      setPendingProfileUpdate(false);
      store.dispatch(
        setUserError(
          new UserUpdateError(USER_USECASE_ERROR_MESSAGES.UPDATE_FAILED)
            .message,
        ),
      );
      console.error('Failed to update user profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            value={user?.email || ''}
            disabled
            variant="outlined"
            sx={{ ...styles.field, ...styles.disabledField }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SecureTextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(value, validation) =>
              handleSecureChange('firstName', value, validation)
            }
            fieldType="name"
            fieldName="First Name"
            variant="outlined"
            required
            showCharacterCounter={true}
            inputProps={{ maxLength: NAME_CHAR_LIMIT }}
            sx={styles.field}
            disabled={isSubmitting || isUserLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SecureTextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(value, validation) =>
              handleSecureChange('lastName', value, validation)
            }
            fieldType="name"
            fieldName="Last Name"
            variant="outlined"
            required
            showCharacterCounter={true}
            inputProps={{ maxLength: NAME_CHAR_LIMIT }}
            sx={styles.field}
            disabled={isSubmitting || isUserLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth sx={styles.field}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender || ''}
              label="Gender"
              onChange={(e) => handleFieldChange('gender', e.target.value)}
              disabled={isSubmitting || isUserLoading}
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
              <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Date of Birth"
            type="date"
            value={
              formData.dateOfBirth ? formData.dateOfBirth.split('T')[0] : ''
            }
            onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: new Date().toISOString().split('T')[0],
            }}
            sx={styles.field}
            disabled={isSubmitting || isUserLoading}
          />
        </Grid>
      </Grid>

      <Box sx={styles.buttonContainer}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          startIcon={<CancelIcon />}
          sx={styles.cancelButton}
          disabled={isSubmitting || isUserLoading}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={
            isSubmitting || !isDirty || isUserLoading || hasSecurityIssues()
          }
          startIcon={
            isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />
          }
          sx={styles.saveButton}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Confirmation Dialog for Discarding Changes */}
      <ConfirmationModal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          setShowConfirmDialog(false);
          resetForm();
        }}
        title="Discard Changes"
        message="Are you sure you want to discard your changes?"
      />
    </Box>
  );
};
